import { useCallback, useEffect } from "react"
import { ZERO_ADDRESS } from "@mezo/sdk"
import { useField } from "formik"

import { RootState, selectToken, useDappSelector } from "#/redux-state"
import { SwapFormField, SwappableToken } from "#/shared/types"
import {
  floatToBigInt,
  getMezoTokenAddress,
  getSwapRoutes,
  isStablePool,
} from "#/shared/utils"

import useSwapGas from "./useSwapGas"

type SwapTokenBalanceProps = {
  amount: string
  tokenIn: SwappableToken
  tokenOut: SwappableToken
  fieldName: SwapFormField
}

const useSwapTokenBalance = ({
  amount,
  tokenIn,
  tokenOut,
  fieldName,
}: SwapTokenBalanceProps) => {
  const { tokenBalance, tokenDecimals } = useDappSelector((state: RootState) =>
    selectToken(state, tokenIn),
  )

  const [, , helpersAmount] = useField(`${fieldName}.amount`)
  const [, metaSlippage] = useField("slippage")

  const tokenInAddress = getMezoTokenAddress(tokenIn)
  const tokenOutAddress = getMezoTokenAddress(tokenOut)

  const { gasPrice } = useSwapGas(
    floatToBigInt(amount, tokenDecimals),
    tokenInAddress ?? ZERO_ADDRESS,
    getSwapRoutes(
      tokenInAddress,
      tokenOutAddress,
      isStablePool(tokenIn, tokenOut),
    ),
    Number(metaSlippage.value),
  )

  const checkSwapTokenBalance = useCallback(() => {
    const normalizedAmount = floatToBigInt(amount, tokenDecimals)

    // For BTC token for max amount we need to include gas price
    const normalizedAmountWithGas =
      tokenIn === "mBTC"
        ? normalizedAmount + (gasPrice ?? 0n)
        : normalizedAmount

    const notEnoughBalance = normalizedAmountWithGas >= tokenBalance

    helpersAmount.setError(notEnoughBalance ? "Insufficient funds" : undefined)
  }, [tokenBalance, helpersAmount, tokenDecimals, gasPrice, tokenIn, amount])

  useEffect(() => {
    checkSwapTokenBalance()
  }, [checkSwapTokenBalance])

  return { checkSwapTokenBalance }
}

export default useSwapTokenBalance
